import { EmailAddress, IOperationResult, IUser } from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import moment from 'moment-timezone';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'sonner';
import { FinancialBrokerDealer } from '../../../../../extViewmodels';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../../../models/AppState';
import { EventLogger, IEventLoggingComponentProps } from '../../../../../models/Logging';
import { baseStyleSheet } from '../../../../styles/styles';
import { TextArea } from '../../../../components-v2/TextArea';
import { TextInput } from '../../../TextInput';
import { ITimezoneData, TimezonePicker } from '../../../TimezonePicker';
import { PersonalSettingsProfilePictureSection } from './presentation';
import { styleSheet } from './styles';

interface IProps
	extends RouteComponentProps<any>,
		IEventLoggingComponentProps,
		IErrorMessageComponentProps,
		IUserSessionComponentProps {
	className?: string;
	expanded?: boolean;
	onToggleExpansion?(expanded: boolean): void;
}

interface IState {
	changeTimezone?: boolean;
	firstName?: string;
	isLoading?: boolean;
	lastName?: string;
	phoneNumber?: string;
	primaryEmail?: EmailAddress;
	selectedTimeZoneOption?: string;
	ceteraDisclosure?: string;
}

class _ProfileSettings extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props);
		const nextState = this.getResetStateWithProps(this.props);
		this.state = {
			isLoading: false,
			...(nextState || {}),
		};
	}

	public render() {
		const { firstName, isLoading, lastName, phoneNumber, primaryEmail } = this.state;
		const { userSession } = this.props;

		if (!firstName && !lastName) {
			return null;
		}

		return (
			<>
				<div className={isLoading ? 'lev-loading-spinner' : 'hide'} />
				<PersonalSettingsProfilePictureSection />
				<section className={isLoading ? 'hide' : 'settings-formContainer'}>
					<div className={css(baseStyleSheet.mb2)}>
						<label className={css(styleSheet.formFieldLabel)}>First Name</label>
						<TextInput
							className={css(styleSheet.inputField)}
							inputId='profileFirstName'
							onChange={this.updateFormFieldValue('firstName')}
							placeholder='First Name (required)'
							type='text'
							value={firstName || ''}
						/>
					</div>
					<div className={css(baseStyleSheet.mb2)}>
						<label className={css(styleSheet.formFieldLabel)}>Last Name</label>
						<TextInput
							className={css(styleSheet.inputField)}
							inputId='profileLastName'
							onChange={this.updateFormFieldValue('lastName')}
							placeholder='Last Name (required)'
							type='text'
							value={lastName || ''}
						/>
					</div>
					<div className={css(baseStyleSheet.mb2)}>
						<label className={css(styleSheet.formFieldLabel)}>Phone Number</label>
						<TextInput
							className={css(styleSheet.inputField)}
							inputId='profilePhoneNumber'
							onChange={this.updateFormFieldValue('phoneNumber')}
							placeholder='(optional)'
							type='text'
							value={phoneNumber || ''}
						/>
					</div>
					<div className={css(baseStyleSheet.mb2)}>
						<span className={css(styleSheet.formFieldLabel)}>Levitate Login</span>
						<div className={css(styleSheet.formValueNonEditable, styleSheet.emailField)}>
							<span>{primaryEmail.value}</span>
							<a
								className={css(baseStyleSheet.brandLink, styleSheet.connectEmailLink)}
								href='mailto:support@levitateapp.com'
							>
								Contact support to change
							</a>
						</div>
					</div>
					<div className={css(baseStyleSheet.mb2)}>
						<span className={css(styleSheet.formFieldLabel)}>&quot;Sending As&quot; Email</span>
						<div className={css(styleSheet.formValueNonEditable, styleSheet.emailField)}>
							<span>
								{userSession?.user?.lastTestEmail?.emailAddress || (
									<span className={css(styleSheet.noTestEmail)}>Not available</span>
								)}
							</span>
							<a
								className={css(baseStyleSheet.brandLink, styleSheet.connectEmailLink)}
								href={window.location.origin + '/#/user/connect-email'}
							>
								Connect to a different email
							</a>
						</div>
					</div>
					{this.renderTimezone()}
					{this.renderCeteraDisclosure()}
				</section>
				<div className={css(styleSheet.buttonContainer)}>
					<button className={css(baseStyleSheet.ctaButton)} onClick={this.saveClick}>
						Save
					</button>
					<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={this.onReset}>
						Cancel
					</button>
				</div>
			</>
		);
	}

	private renderCeteraDisclosure() {
		const { userSession } = this.props;
		const { ceteraDisclosure } = this.state;

		// Only show for Cetera broker dealer accounts
		const showCeteraDisclosure = userSession.account.additionalInfo.brokerDealer === FinancialBrokerDealer.Cetera;

		if (!showCeteraDisclosure) {
			return null;
		}

		return (
			<div className={css(baseStyleSheet.mb2)}>
				<label className={css(styleSheet.ceteraDisclosureLabel)}>Cetera Disclosure</label>
				<TextArea
					styles={[styleSheet.ceteraDisclosureField]}
					id='ceteraDisclosure'
					onChange={this.onCeteraDisclosureChange}
					value={ceteraDisclosure || ''}
				/>
			</div>
		);
	}

	private renderTimezone() {
		const { userSession } = this.props;
		const { changeTimezone, selectedTimeZoneOption } = this.state;

		const content = changeTimezone ? (
			<span>
				<TimezonePicker styles={[styleSheet.inputField]} onTimezoneSelected={this.onTimeZoneChanged} />
				<button
					className={css(baseStyleSheet.brandLink, styleSheet.changeTimezoneBtn)}
					onClick={this.onUserSystemDefaultClick}
				>
					Use system default
				</button>
				<button
					className={css(baseStyleSheet.brandLink, styleSheet.changeTimezoneBtn)}
					onClick={this.onCancelTimezoneClick}
				>
					Cancel
				</button>
			</span>
		) : (
			<span className={css(styleSheet.formValueNonEditable, styleSheet.emailField)}>
				{selectedTimeZoneOption || userSession?.user?.userPreferences?.timeZone || moment.tz.guess()}
				<button
					className={css(baseStyleSheet.brandLink, styleSheet.changeTimezoneBtn)}
					onClick={this.onChangeTimezoneClick}
				>
					Change
				</button>
			</span>
		);

		return (
			<div className={css(baseStyleSheet.mb2)}>
				<span className={css(styleSheet.formFieldLabel)}>Time Zone</span>
				{content}
			</div>
		);
	}

	private onUserSystemDefaultClick = () => {
		this.setState({
			changeTimezone: false,
			selectedTimeZoneOption: moment.tz.guess(),
		});
	};

	private onCancelTimezoneClick = () => {
		this.setState({
			changeTimezone: false,

			selectedTimeZoneOption: null,
		});
	};

	private onChangeTimezoneClick = () => {
		this.setState({ changeTimezone: true });
	};

	private onTimeZoneChanged = (tz: ITimezoneData) => {
		this.setState({
			changeTimezone: false,
			selectedTimeZoneOption: tz.name,
		});
	};

	private updateFormFieldValue = (propName: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
		const nextState: IState & Record<string, string> = {};
		nextState[propName] = e.target.value;
		this.setState(nextState);
	};

	private onCeteraDisclosureChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		this.setState({ ceteraDisclosure: e.target.value });
	};

	private displayErrorMessages = (errorMessages: string[]) => {
		if (this.props.errorMessages) {
			this.props.errorMessages.push({
				messages: errorMessages,
			});
		}
	};

	private saveClick = () => {
		const { userSession } = this.props;
		// Validate required fields
		let isValid = true;
		const errorMessages: string[] = [];
		const firstName = (this.state.firstName || '').trim();
		const lastName = (this.state.lastName || '').trim();
		const phoneNumber = (this.state.phoneNumber || '').trim();
		if (!firstName) {
			errorMessages.push('First name is required');
			isValid = false;
		}
		if (!lastName) {
			errorMessages.push('Last name is required');
			isValid = false;
		}

		if (!isValid) {
			this.displayErrorMessages(errorMessages);
			return;
		}

		// Call a web service to update the current user
		const user: IUser = {
			...userSession.user,
			firstName,
			lastName,
			mobilePhone: phoneNumber ? phoneNumber : '',
			userPreferences: {
				...(userSession.user.userPreferences || {}),
				timeZone: this.state.selectedTimeZoneOption,
				templatePreferences: {
					...(userSession.user.userPreferences?.templatePreferences || {}),
					firstTextMessageSignature: this.state.ceteraDisclosure || '',
				},
			},
		};

		userSession.webServiceHelper.callWebServiceWithOperationResults<IUser>(
			`user/${userSession.user.id}`, // URL
			'PUT', // Method
			user,
			this.saveSuccess,

			result => this.displayErrorMessages([result.systemMessage])
		);

		this.setState({ isLoading: true });
	};

	private saveSuccess = (operationResult: IOperationResult<IUser>) => {
		const { firstName, lastName, mobilePhone } = operationResult.value;

		this.props.userSession.updateUser({
			...this.props.userSession.user,
			firstName,
			lastName,
			mobilePhone,
		});
		this.setState({
			firstName,
			isLoading: false,
			lastName,
			phoneNumber: mobilePhone,
			ceteraDisclosure: operationResult.value.userPreferences?.templatePreferences?.firstTextMessageSignature || '',
		});

		toast.success('Changes were saved successfully!');

		EventLogger.logEvent({
			action: 'ProfileInfo-Changed',
			category: 'Settings-Personal',
		});
	};

	private getResetStateWithProps = (props: IProps) => {
		const user = props.userSession.user;
		if (user) {
			let selectedTimeZoneOption: string = null;

			if (user.userPreferences.timeZone) {
				const timeZone = user.userPreferences.timeZone;
				selectedTimeZoneOption = timeZone;
			}
			const nextState: IState = {
				firstName: user.firstName,
				lastName: user.lastName,
				phoneNumber: user.mobilePhone,
				primaryEmail: user?.primaryEmail ? { ...user.primaryEmail } : null,
				selectedTimeZoneOption,
				ceteraDisclosure: user.userPreferences?.templatePreferences?.firstTextMessageSignature || '',
			};
			return nextState;
		}
		return null;
	};

	private onReset = () => {
		const nextState = this.getResetStateWithProps(this.props);
		if (nextState) {
			this.setState(nextState);
		}
		// Close the accordion when Cancel is clicked
		this.props.onToggleExpansion?.(false);
	};
}

const ProfileSettingsAsObserver = observer(_ProfileSettings);
const ProfileSettingsWithRouter = withRouter(ProfileSettingsAsObserver);
export const ProfileSettings = inject(ErrorMessagesViewModelKey, UserSessionViewModelKey)(ProfileSettingsWithRouter);
